import { FC, useEffect } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { EmailTemplateFormProps, EmailTemplateFormInput } from './types';
import { emailFormInputs } from './emailFormInput.map';
import { useStyles } from './styles';
import { useForm, Controller } from 'react-hook-form';
import CloseIcon from '@material-ui/icons/Close';
import { TextField, Button, Grid, Modal, Paper, IconButton } from '@material-ui/core';
import { uid } from 'utils';

export const EmailTemplateForm: FC<EmailTemplateFormProps> = ({ open, refetch, handleClose }) => {
  const classes = useStyles();

  const {
    emailTemplatesTableStore: { selectedEmailTemplate, updateEmailTemplate },
    notificationStore: { openNotification },
  } = useStore();

  const updateEmailTemplateReq = useMutation((data: EmailTemplateFormInput) => updateEmailTemplate(data), {
    mutationKey: 'updateEmailTemplateReq',
    onSuccess: () => {
      openNotification('success', `Successfully updated`);
      refetch();
      reset();
      handleClose();
    },
    onError: () => openNotification('error', `Can't update template`),
  });

  useAppPreloader(updateEmailTemplateReq.isLoading);

  const { control, handleSubmit, reset } = useForm<EmailTemplateFormInput>({
    mode: 'onChange',
  });

  useEffect(() => {
    if (open && selectedEmailTemplate) {
      reset({
        ...selectedEmailTemplate,
        bccTo: selectedEmailTemplate.bccTo ?? '',
        status:
          typeof selectedEmailTemplate.status === 'boolean'
            ? selectedEmailTemplate.status
              ? 1
              : 0
            : selectedEmailTemplate.status,
      });
    }
  }, [open, selectedEmailTemplate, reset]);

  const onSubmit = (data: EmailTemplateFormInput) => updateEmailTemplateReq.mutate(data);

  return (
    <Modal className={classes.modal} disablePortal disableEnforceFocus disableAutoFocus open={open}>
      <Paper className={classes.paper} elevation={3}>
        <Grid container justify='space-between' alignItems='center'>
          <p className={classes.label}>Edit Email Template</p>
          <IconButton
            style={{ marginRight: -16 }}
            onClick={() => {
              reset();
              handleClose();
            }}
          >
            <CloseIcon color='primary' />
          </IconButton>
        </Grid>
        <form onSubmit={handleSubmit(onSubmit)}>
          {emailFormInputs.map((input) => (
            <Grid className={classes.row} key={uid()} container>
              <Grid item xs={12} sm={4}>
                <p className={classes.title}>{input.title}</p>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Controller
                  name={input.name}
                  control={control}
                  defaultValue={
                    (selectedEmailTemplate?.[input.name] === null
                      ? ''
                      : typeof selectedEmailTemplate?.[input.name] === 'boolean'
                      ? selectedEmailTemplate?.[input.name]
                        ? 1
                        : 0
                      : selectedEmailTemplate?.[input.name]) as string | number | undefined
                  }
                  rules={{
                    required: input?.required ? 'This field is required' : false,
                  }}
                  render={({ field, fieldState: { invalid } }) =>
                    input.name === 'templateBody' ? (
                      <div
                        className={classes.textFields}
                        contentEditable
                        dangerouslySetInnerHTML={{ __html: field.value || '' }}
                        onBlur={(e) => field.onChange(e.currentTarget.innerHTML)}
                      />
                    ) : (
                      <TextField
                        {...field}
                        className={classes.textField}
                        error={invalid && !field.value}
                        helperText={invalid && !field.value ? 'Required field' : ''}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        InputProps={
                          ['sendTo', 'bccTo'].includes(input.name) ? { style: { color: '#1976d2' } } : undefined
                        }
                      />
                    )
                  }
                />
              </Grid>
            </Grid>
          ))}
          <Grid className={classes.keywordTitle} container>
            <p className={classes.title}>TEMPLATE KEYWORDS</p>
            <p className={classes.keywords}>{selectedEmailTemplate?.templateKeywords}</p>
          </Grid>
          <Button
            className={classes.btn}
            type='submit'
            fullWidth
            variant='contained'
            color='primary'
            disabled={updateEmailTemplateReq.isLoading}
          >
            {updateEmailTemplateReq.isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </Paper>
    </Modal>
  );
};
